import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Row,
} from "../../../components/Component";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import http from "../../../utils/http-common";
import swal from "sweetalert";
import { getPublicUrl } from "../../../utils/Utils";
import SlotAvailabilityCalendar from "./clinic-calendar/SlotAvailabilityCalendar";
import { buildRecurringSlotPayloads } from "./clinic-calendar/buildRecurringSlots";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function defaultYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function toDatetimeLocalValue(d) {
  if (!d || Number.isNaN(new Date(d).getTime())) return "";
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

function mapHttpError(err, fallback) {
  const status = err.response?.status;
  const raw = err.response?.data?.message || err.response?.data?.error;
  const s = typeof raw === "string" ? raw : raw != null ? JSON.stringify(raw) : "";
  const lower = s.toLowerCase();
  if (status === 401 || lower.includes("unauthorized")) {
    return "Calendar rejected credentials (unauthorized). Check API token or OAuth refresh token.";
  }
  if (status === 403) {
    return s || "You are not allowed to perform this action.";
  }
  if (lower.includes("quota") || lower.includes("rate limit")) {
    return "External calendar API quota or rate limit exceeded. Try again later.";
  }
  if (lower.includes("not configured") || lower.includes("sync not configured")) {
    return s || "Calendar sync is not configured for this clinic.";
  }
  if (lower.includes("invalid") && lower.includes("token")) {
    return s || "Invalid token. Verify Calendly API token or Google refresh token.";
  }
  return s || fallback;
}

/**
 * Admin: availability hub — calendar view, slots CRUD, external sync (Calendly / Google).
 * Route: /clinics/:clinicId/calendar
 */
const ClinicCalendarAdmin = () => {
  const { clinicId } = useParams();
  const history = useHistory();
  const visibleRangeRef = useRef(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [practitioners, setPractitioners] = useState([]);
  const [slots, setSlots] = useState([]);
  const [calendlyToken, setCalendlyToken] = useState("");
  const [calendlyEventUri, setCalendlyEventUri] = useState("");
  const [showCalendlyTokenField, setShowCalendlyTokenField] = useState(true);
  const [googleCalendarId, setGoogleCalendarId] = useState("primary");
  const [googleRefreshToken, setGoogleRefreshToken] = useState("");
  const [showGoogleTokenField, setShowGoogleTokenField] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingGoogle, setSavingGoogle] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [creatingSlot, setCreatingSlot] = useState(false);
  const [message, setMessage] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailSlot, setDetailSlot] = useState(null);
  const [slotStartLocal, setSlotStartLocal] = useState("");
  const [slotEndLocal, setSlotEndLocal] = useState("");
  const [slotPractitionerId, setSlotPractitionerId] = useState("");
  const [slotFilterPractitionerId, setSlotFilterPractitionerId] = useState("");
  const [generatingSlots, setGeneratingSlots] = useState(false);
  const [buildDateFrom, setBuildDateFrom] = useState(() => defaultYmd(new Date()));
  const [buildDateTo, setBuildDateTo] = useState(() => {
    const t = new Date();
    t.setDate(t.getDate() + 14);
    return defaultYmd(t);
  });
  const [weekdaysSunFirst, setWeekdaysSunFirst] = useState([
    false,
    true,
    true,
    true,
    true,
    true,
    false,
  ]);
  const [buildTimeStart, setBuildTimeStart] = useState("09:00");
  const [buildTimeEnd, setBuildTimeEnd] = useState("17:00");
  const [buildDuration, setBuildDuration] = useState(30);
  const [buildPractitionerId, setBuildPractitionerId] = useState("");

  const loadSlots = useCallback(
    (rangeStart, rangeEnd) => {
      if (!clinicId) return Promise.resolve();
      setSlotsLoading(true);
      const qs = new URLSearchParams({
        dateFrom: rangeStart.toISOString(),
        dateTo: rangeEnd.toISOString(),
        status: "all",
      });
      if (slotFilterPractitionerId) {
        qs.set("practitionerId", slotFilterPractitionerId);
      }
      return http
        .get(`/api/v1/consultations/clinics/${clinicId}/slots?${qs.toString()}`)
        .then((res) => {
          const list = res.data?.data;
          setSlots(Array.isArray(list) ? list : []);
        })
        .catch((e) => {
          setMessage({
            type: "error",
            text: e.response?.data?.message || "Failed to load slots.",
          });
          setSlots([]);
        })
        .finally(() => setSlotsLoading(false));
    },
    [clinicId, slotFilterPractitionerId]
  );

  const reloadVisibleRange = useCallback(() => {
    const r = visibleRangeRef.current;
    if (r) {
      return loadSlots(r.start, r.end);
    }
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setDate(to.getDate() + 30);
    return loadSlots(from, to);
  }, [loadSlots]);

  useEffect(() => {
    if (!clinicId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    http
      .get(`/api/v1/partners/medical/clinics/${clinicId}`)
      .then((res) => {
        const c = res.data?.data || res.data;
        setClinic(c);
        setCalendlyEventUri(c?.externalCalendarEventTypeUri || "");
        setGoogleCalendarId(c?.externalCalendarCalendarId || "primary");
        setShowCalendlyTokenField(c?.calendarCredentialsConfigured !== true);
        setShowGoogleTokenField(false);
      })
      .catch((e) => {
        swal("Error", e.response?.data?.message || "Failed to load clinic", "error");
        setClinic(null);
      })
      .finally(() => setLoading(false));
  }, [clinicId]);

  useEffect(() => {
    if (!clinicId) return;
    http
      .get(`/api/v1/partners/medical/clinics/${clinicId}/practitioners/assignable`)
      .then((res) => {
        const list = res.data?.data;
        setPractitioners(Array.isArray(list) ? list : []);
      })
      .catch(() => setPractitioners([]));
  }, [clinicId]);

  const connected = clinic?.syncEnabled && clinic?.externalCalendarProvider;
  const credentialsConfigured = clinic?.calendarCredentialsConfigured === true;
  const providerLabel =
    clinic?.externalCalendarProvider === "calendly"
      ? "Calendly"
      : clinic?.externalCalendarProvider === "google_calendar"
        ? "Google Calendar"
        : null;
  const tz = clinic?.timezone || "";

  const navigateBackFromCalendar = useCallback(() => {
    const pid = clinic?.partner?._id || clinic?.partner;
    if (pid) {
      history.push(getPublicUrl(`medical-partner-details/${String(pid)}`));
      return;
    }
    history.push(getPublicUrl("clinics"));
  }, [history, clinic]);

  const handleDatesSet = useCallback(
    (start, end) => {
      visibleRangeRef.current = { start, end };
      loadSlots(start, end);
    },
    [loadSlots]
  );

  useEffect(() => {
    if (!clinicId) return;
    reloadVisibleRange();
  }, [slotFilterPractitionerId, clinicId, reloadVisibleRange]);

  const handleCalendarSelect = (start, end) => {
    setSlotStartLocal(toDatetimeLocalValue(start));
    setSlotEndLocal(toDatetimeLocalValue(end));
    setSlotPractitionerId(slotFilterPractitionerId || "");
    setCreateModalOpen(true);
  };

  const handleEventClickSlot = (slot) => {
    setDetailSlot(slot);
    setDetailModalOpen(true);
  };

  const handleConnectCalendly = (e) => {
    e.preventDefault();
    if (!clinicId) return;
    const tokenTrim = calendlyToken.trim();
    const uriTrim = calendlyEventUri.trim();
    if (!credentialsConfigured && !tokenTrim) {
      setMessage({ type: "error", text: "Enter your Calendly personal access token (or use Change token if replacing)." });
      return;
    }
    if (uriTrim && !uriTrim.startsWith("https://api.calendly.com/")) {
      setMessage({
        type: "error",
        text: "Event type URI must start with https://api.calendly.com/",
      });
      return;
    }
    setSaving(true);
    setMessage(null);
    const payload = {
      externalCalendarProvider: "calendly",
      externalCalendarEventTypeUri: uriTrim || undefined,
      syncEnabled: !!(tokenTrim || credentialsConfigured),
    };
    if (tokenTrim) {
      payload.externalCalendarCredentials = JSON.stringify({ apiToken: tokenTrim });
    }
    http
      .put(`/api/v1/partners/medical/clinics/${clinicId}`, payload)
      .then((res) => {
        setClinic(res.data?.data || res.data);
        setCalendlyToken("");
        setShowCalendlyTokenField(false);
        setMessage({ type: "success", text: "Calendly settings saved." });
      })
      .catch((err) => {
        setMessage({ type: "error", text: mapHttpError(err, "Failed to save.") });
      })
      .finally(() => setSaving(false));
  };

  const handleSaveGoogle = (e) => {
    e.preventDefault();
    if (!clinicId) return;
    const idTrim = googleCalendarId.trim() || "primary";
    const rtTrim = googleRefreshToken.trim();
    setSavingGoogle(true);
    setMessage(null);

    const payload = {
      externalCalendarProvider: "google_calendar",
      externalCalendarCalendarId: idTrim,
    };

    if (rtTrim) {
      payload.externalCalendarCredentials = JSON.stringify({ refreshToken: rtTrim });
      payload.syncEnabled = true;
    } else {
      if (!credentialsConfigured) {
        setMessage({
          type: "error",
          text: "Enter a Google OAuth refresh token to connect Google Calendar the first time.",
        });
        setSavingGoogle(false);
        return;
      }
      payload.syncEnabled = clinic?.syncEnabled === true;
    }

    http
      .put(`/api/v1/partners/medical/clinics/${clinicId}`, payload)
      .then((res) => {
        setClinic(res.data?.data || res.data);
        setGoogleRefreshToken("");
        setShowGoogleTokenField(false);
        setMessage({ type: "success", text: "Google Calendar settings saved." });
      })
      .catch((err) => {
        setMessage({ type: "error", text: mapHttpError(err, "Failed to save Google settings.") });
      })
      .finally(() => setSavingGoogle(false));
  };

  const handleDisconnect = () => {
    if (!clinicId) return;
    swal({
      title: "Disconnect calendar?",
      text: "External calendar sync will stop until you connect again.",
      icon: "warning",
      buttons: ["Cancel", "Disconnect"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      setSaving(true);
      setMessage(null);
      http
        .put(`/api/v1/partners/medical/clinics/${clinicId}`, {
          externalCalendarProvider: null,
          externalCalendarEventTypeUri: null,
          externalCalendarCredentials: null,
          externalCalendarCalendarId: null,
          syncEnabled: false,
        })
        .then((res) => {
          setClinic(res.data?.data || res.data);
          setCalendlyToken("");
          setCalendlyEventUri("");
          setGoogleRefreshToken("");
          setShowCalendlyTokenField(true);
          setMessage({ type: "success", text: "Calendar disconnected." });
        })
        .catch((err) => {
          setMessage({ type: "error", text: err.response?.data?.message || "Failed to disconnect." });
        })
        .finally(() => setSaving(false));
    });
  };

  const handleSyncNow = () => {
    if (!clinicId) return;
    setSyncing(true);
    setMessage(null);
    http
      .post(`/api/v1/consultations/clinics/${clinicId}/sync`, {})
      .then((res) => {
        const stats = res.data?.data;
        const created = stats?.created ?? 0;
        const updated = stats?.updated ?? 0;
        const errors = stats?.errors ?? 0;
        const skippedBooked = stats?.skippedBookedOverlap ?? 0;
        const externalIntervals = stats?.externalIntervals ?? stats?.externalEvents ?? null;
        const rangeDays = stats?.rangeDays != null ? String(stats.rangeDays) : null;
        const windowStart = stats?.windowStart ? new Date(stats.windowStart).toLocaleString() : null;
        const windowEnd = stats?.windowEnd ? new Date(stats.windowEnd).toLocaleString() : null;
        const providerRaw = stats?.provider || clinic?.externalCalendarProvider;
        const providerName =
          providerRaw === "calendly"
            ? "Calendly"
            : providerRaw === "google_calendar"
              ? "Google Calendar"
              : providerRaw || "External";
        const windowPart =
          windowStart && windowEnd
            ? `Window: ${windowStart} → ${windowEnd}${rangeDays ? ` (${rangeDays} days ahead)` : ""}.`
            : rangeDays
              ? `Rolling window: next ${rangeDays} days.`
              : "";
        const extPart =
          externalIntervals != null
            ? `External free/busy intervals fetched: ${externalIntervals}.`
            : "";
        setMessage({
          type: "success",
          text: [
            `${providerName} sync complete.`,
            windowPart,
            extPart,
            `Slots: ${created} created, ${updated} overlapping available (unchanged), ${skippedBooked} skipped (booked overlap).`,
            `Errors: ${errors}.`,
          ]
            .filter(Boolean)
            .join(" "),
        });
        return http.get(`/api/v1/partners/medical/clinics/${clinicId}`);
      })
      .then((res) => {
        if (res) setClinic(res.data?.data || res.data);
        return reloadVisibleRange();
      })
      .catch((err) => {
        setMessage({ type: "error", text: mapHttpError(err, "Sync failed.") });
      })
      .finally(() => setSyncing(false));
  };

  const submitCreateSlot = (e) => {
    e.preventDefault();
    if (!clinicId) return;
    if (!slotStartLocal || !slotEndLocal) {
      setMessage({ type: "error", text: "Choose start and end date/time for the slot." });
      return;
    }
    const startAt = new Date(slotStartLocal);
    const endAt = new Date(slotEndLocal);
    if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
      setMessage({ type: "error", text: "Invalid date/time." });
      return;
    }
    if (endAt <= startAt) {
      setMessage({ type: "error", text: "End must be after start." });
      return;
    }
    if (startAt <= new Date()) {
      setMessage({ type: "error", text: "Start time must be in the future." });
      return;
    }
    const body = {
      startAt: startAt.toISOString(),
      endAt: endAt.toISOString(),
    };
    if (slotPractitionerId) {
      body.practitioner = slotPractitionerId;
    }
    setCreatingSlot(true);
    setMessage(null);
    http
      .post(`/api/v1/consultations/clinics/${clinicId}/slots`, body)
      .then(() => {
        setMessage({ type: "success", text: "Slot created." });
        setCreateModalOpen(false);
        setSlotStartLocal("");
        setSlotEndLocal("");
        setSlotPractitionerId("");
        return reloadVisibleRange();
      })
      .catch((err) => {
        setMessage({ type: "error", text: mapHttpError(err, "Failed to create slot.") });
      })
      .finally(() => setCreatingSlot(false));
  };

  const handleDeleteSlot = (slot) => {
    if (!clinicId || !slot?._id) return;
    if (slot.status !== "available") {
      swal("Not allowed", "Only available slots can be deleted.", "info");
      return;
    }
    swal({
      title: "Delete this slot?",
      text: "Patients will no longer see it if it was still available.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      http
        .delete(`/api/v1/consultations/clinics/${clinicId}/slots/${slot._id}`)
        .then(() => {
          setMessage({ type: "success", text: "Slot deleted." });
          setDetailModalOpen(false);
          setDetailSlot(null);
          return reloadVisibleRange();
        })
        .catch((err) => {
          swal("Error", err.response?.data?.message || "Failed to delete slot.", "error");
        });
    });
  };

  const toggleWeekday = (index) => {
    setWeekdaysSunFirst((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const handleGenerateSlots = (e) => {
    e.preventDefault();
    if (!clinicId) return;
    const built = buildRecurringSlotPayloads(
      buildDateFrom,
      buildDateTo,
      weekdaysSunFirst,
      buildTimeStart,
      buildTimeEnd,
      Number(buildDuration),
      buildPractitionerId || undefined
    );
    if (built.error && built.slots.length === 0) {
      swal("Cannot generate", built.error, "error");
      return;
    }
    if (built.slots.length === 0) {
      swal(
        "No slots",
        built.skippedPast
          ? `All candidate slots would be in the past (${built.skippedPast} skipped).`
          : "No slots were generated.",
        "info"
      );
      return;
    }
    const confirmText =
      built.slots.length > 50
        ? `Create ${built.slots.length} slots in this clinic?`
        : null;
    const run = () => {
      setGeneratingSlots(true);
      setMessage(null);
      http
        .post(`/api/v1/consultations/clinics/${clinicId}/slots`, { slots: built.slots })
        .then((res) => {
          const n = res.data?.count ?? built.slots.length;
          let text = `Created ${n} slot(s).`;
          if (built.skippedPast) text += ` Skipped ${built.skippedPast} in the past.`;
          if (built.error) text += ` ${built.error}`;
          setMessage({ type: "success", text });
          return reloadVisibleRange();
        })
        .catch((err) => {
          setMessage({ type: "error", text: mapHttpError(err, "Failed to create slots.") });
        })
        .finally(() => setGeneratingSlots(false));
    };
    if (confirmText) {
      swal({
        title: "Confirm batch",
        text: confirmText,
        icon: "warning",
        buttons: ["Cancel", "Create"],
        dangerMode: false,
      }).then((ok) => {
        if (ok) run();
      });
    } else {
      run();
    }
  };

  if (loading) {
    return (
      <Content>
        <Head title="Clinic calendar" />
        <Block>
          <div className="d-flex align-items-center text-soft">
            <div className="spinner-border spinner-border-sm text-primary mr-2" role="status" />
            Loading…
          </div>
        </Block>
      </Content>
    );
  }

  if (!clinic) {
    return (
      <Content>
        <Head title="Clinic calendar" />
        <Block>
          <p>Clinic not found.</p>
          <Button color="light" onClick={() => history.push(getPublicUrl("clinics"))}>
            Back to clinics
          </Button>
        </Block>
      </Content>
    );
  }

  return (
    <Content>
      <Head title={`Calendar — ${clinic.name || "Clinic"}`} />
      <BlockHead size="sm">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle tag="h3">Teleconsultation calendar (admin)</BlockTitle>
            <BlockDes className="text-soft">
              Manage availability for <strong>{clinic.name || "this clinic"}</strong>: use the calendar to view slots, drag to
              create, or sync from an external calendar.
            </BlockDes>
            {tz ? (
              <p className="text-soft small mb-0 mt-2">
                <strong>Clinic timezone:</strong> {tz}
              </p>
            ) : (
              <p className="text-soft small mb-0 mt-2">No IANA timezone set on this clinic — calendar uses your browser time.</p>
            )}
          </BlockHeadContent>
          <BlockHeadContent className="mb-xs-3 d-flex flex-wrap flex-g-2">
            <Button color="light" size="sm" onClick={navigateBackFromCalendar}>
              {clinic?.partner?._id || clinic?.partner ? "Back to partner" : "Clinics list"}
            </Button>
            <Button color="light" outline size="sm" onClick={() => history.push(getPublicUrl("clinics"))}>
              All clinics
            </Button>
          </BlockHeadContent>
        </BlockBetween>
      </BlockHead>

      <Block>
        {message && (
          <div
            className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"} mb-3`}
            role="alert"
          >
            {message.text}
          </div>
        )}

        {clinic?.offersTeleconsultation === true && !connected ? (
          <div className="alert alert-warning mb-3" role="status">
            Teleconsultation is enabled, but <strong>no external calendar is connected</strong>. You can still add platform
            slots on the calendar; sync requires Calendly or Google below.
          </div>
        ) : null}

        <Row className="g-gs mb-4">
          <Col md="12">
            <BlockTitle tag="h5">Availability calendar</BlockTitle>
            <p className="text-soft small">
              Select an empty area in week or day view to create a slot. Click an event for details. Month view is read-only
              for navigation — switch to week or day to create.
            </p>
            <Row className="g-gs align-items-end mb-3">
              <Col md="6" lg="4">
                <div className="form-group mb-0">
                  <label className="form-label" htmlFor="cal-slot-filter-pract">
                    Filter by practitioner
                  </label>
                  <select
                    id="cal-slot-filter-pract"
                    className="form-control"
                    value={slotFilterPractitionerId}
                    onChange={(e) => setSlotFilterPractitionerId(e.target.value)}
                  >
                    <option value="">All practitioners</option>
                    {practitioners.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.displayName || p._id}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              <Col md="12" className="mt-2">
                <p className="text-soft small mb-0">
                  Calendar is for <strong>{clinic.name || "this clinic"}</strong> only. The filter limits which slots are loaded;
                  creation still uses the same clinic.
                </p>
              </Col>
            </Row>
            <SlotAvailabilityCalendar
              slots={slots}
              slotsLoading={slotsLoading}
              timeZone={tz}
              onDatesSet={handleDatesSet}
              onSelectSlot={handleCalendarSelect}
              onEventClick={handleEventClickSlot}
            />
          </Col>
        </Row>

        <Row className="g-gs mb-4">
          <Col md="12">
            <BlockTitle tag="h5">Generate recurring slots</BlockTitle>
            <p className="text-soft small">
              Build many platform slots from a date range, weekdays, daily time window, and slot length. Times use your browser
              timezone (same as manual creation). Maximum 200 slots per run.
            </p>
            <form onSubmit={handleGenerateSlots}>
              <Row className="g-gs">
                <Col sm="6" md="4" lg="3">
                  <div className="form-group">
                    <label className="form-label" htmlFor="build-from">
                      From date
                    </label>
                    <input
                      id="build-from"
                      type="date"
                      className="form-control"
                      value={buildDateFrom}
                      onChange={(e) => setBuildDateFrom(e.target.value)}
                      required
                    />
                  </div>
                </Col>
                <Col sm="6" md="4" lg="3">
                  <div className="form-group">
                    <label className="form-label" htmlFor="build-to">
                      To date
                    </label>
                    <input
                      id="build-to"
                      type="date"
                      className="form-control"
                      value={buildDateTo}
                      onChange={(e) => setBuildDateTo(e.target.value)}
                      required
                    />
                  </div>
                </Col>
                <Col sm="6" md="4" lg="3">
                  <div className="form-group">
                    <label className="form-label" htmlFor="build-dur">
                      Slot duration (minutes)
                    </label>
                    <input
                      id="build-dur"
                      type="number"
                      min={10}
                      max={240}
                      className="form-control"
                      value={buildDuration}
                      onChange={(e) => setBuildDuration(e.target.value)}
                      required
                    />
                  </div>
                </Col>
                <Col sm="6" md="4" lg="3">
                  <div className="form-group">
                    <label className="form-label" htmlFor="build-pract-gen">
                      Practitioner (optional)
                    </label>
                    <select
                      id="build-pract-gen"
                      className="form-control"
                      value={buildPractitionerId}
                      onChange={(e) => setBuildPractitionerId(e.target.value)}
                    >
                      <option value="">— Unassigned —</option>
                      {practitioners.map((p) => (
                        <option key={p._id} value={p._id}>
                          {p.displayName || p._id}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
                <Col md="6" lg="4">
                  <div className="form-group">
                    <label className="form-label" htmlFor="build-t0">
                      Window start
                    </label>
                    <input
                      id="build-t0"
                      type="time"
                      className="form-control"
                      value={buildTimeStart}
                      onChange={(e) => setBuildTimeStart(e.target.value)}
                      required
                    />
                  </div>
                </Col>
                <Col md="6" lg="4">
                  <div className="form-group">
                    <label className="form-label" htmlFor="build-t1">
                      Window end
                    </label>
                    <input
                      id="build-t1"
                      type="time"
                      className="form-control"
                      value={buildTimeEnd}
                      onChange={(e) => setBuildTimeEnd(e.target.value)}
                      required
                    />
                  </div>
                </Col>
                <Col md="12">
                  <p className="form-label mb-1">Weekdays (Sun–Sat)</p>
                  <div className="d-flex flex-wrap">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label, i) => (
                      <label key={label} className="custom-control custom-checkbox mb-0 mr-3 mb-2">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          checked={weekdaysSunFirst[i]}
                          onChange={() => toggleWeekday(i)}
                        />
                        <span className="custom-control-label ms-1">{label}</span>
                      </label>
                    ))}
                  </div>
                </Col>
                <Col md="12" className="mt-2">
                  <Button color="primary" type="submit" disabled={generatingSlots}>
                    {generatingSlots ? "Creating…" : "Generate slots"}
                  </Button>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>

        <Row className="g-gs">
          <Col md="12">
            <p className="text-soft small mb-3">
              External sync:{" "}
              {connected ? (
                <>
                  <span className="text-success">Connected — {providerLabel}</span>
                  {clinic.lastSyncAt && (
                    <span className="ms-2">
                      Last sync:{" "}
                      {new Date(clinic.lastSyncAt).toLocaleString(undefined, tz ? { timeZone: tz } : {})}
                    </span>
                  )}
                </>
              ) : (
                "No external calendar connected."
              )}
            </p>
            {connected && (
              <Button color="primary" className="me-2" onClick={handleSyncNow} disabled={syncing}>
                {syncing ? "Syncing…" : "Sync now"}
              </Button>
            )}
          </Col>

          <Col md="12" className="mt-3">
            <BlockTitle tag="h5">Calendly</BlockTitle>
            <p className="text-soft small">
              Personal access token: Calendly → Integrations → API &amp; webhooks → Generate new token. Event type URI: open
              your event type in Calendly and copy the API URI (starts with{" "}
              <code>https://api.calendly.com/event_types/</code>).
            </p>
            {credentialsConfigured && clinic?.externalCalendarProvider === "calendly" ? (
              <p className="small text-success mb-2">API token is stored on the server.</p>
            ) : credentialsConfigured ? (
              <p className="small text-muted mb-2">Credentials are stored for the current provider. Switching provider updates storage.</p>
            ) : null}
            {!showCalendlyTokenField && credentialsConfigured ? (
              <Button
                color="light"
                size="sm"
                className="mb-2"
                onClick={() => {
                  setShowCalendlyTokenField(true);
                  setCalendlyToken("");
                }}
              >
                Change API token
              </Button>
            ) : null}
            <form onSubmit={handleConnectCalendly}>
              {(showCalendlyTokenField || !credentialsConfigured) && (
                <div className="form-group mb-2">
                  <label className="form-label" htmlFor="cal-token">
                    API token
                  </label>
                  <input
                    id="cal-token"
                    type="password"
                    className="form-control"
                    value={calendlyToken}
                    onChange={(e) => setCalendlyToken(e.target.value)}
                    placeholder={credentialsConfigured ? "Paste new token to replace" : "Calendly personal access token"}
                    autoComplete="off"
                  />
                </div>
              )}
              <div className="form-group mb-2">
                <label className="form-label" htmlFor="cal-uri">
                  Event type URI
                </label>
                <input
                  id="cal-uri"
                  type="text"
                  className="form-control"
                  value={calendlyEventUri}
                  onChange={(e) => setCalendlyEventUri(e.target.value)}
                  placeholder="https://api.calendly.com/event_types/..."
                />
              </div>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? "Saving…" : "Save Calendly"}
              </Button>
              {showCalendlyTokenField && credentialsConfigured ? (
                <Button
                  color="link"
                  type="button"
                  className="ml-2"
                  onClick={() => {
                    setShowCalendlyTokenField(false);
                    setCalendlyToken("");
                  }}
                >
                  Cancel token change
                </Button>
              ) : null}
            </form>
          </Col>

          <Col md="12" className="mt-4">
            <BlockTitle tag="h5">Google Calendar</BlockTitle>
            <p className="text-soft small">
              Use OAuth refresh token and calendar ID for this clinic. Platform-wide Google client ID/secret are configured in{" "}
              <Button
                color="link"
                className="p-0 align-baseline"
                type="button"
                onClick={() => history.push(getPublicUrl("integrations"))}
              >
                Platform integrations
              </Button>
              . Typical calendar ID is <code>primary</code> or an ID from Google Calendar settings.
            </p>
            {credentialsConfigured && clinic?.externalCalendarProvider === "google_calendar" ? (
              <p className="small text-success mb-2">Refresh token is stored on the server.</p>
            ) : null}
            {!showGoogleTokenField && credentialsConfigured && clinic?.externalCalendarProvider === "google_calendar" ? (
              <Button
                color="light"
                size="sm"
                className="mb-2"
                type="button"
                onClick={() => {
                  setShowGoogleTokenField(true);
                  setGoogleRefreshToken("");
                }}
              >
                Change refresh token
              </Button>
            ) : null}
            <form onSubmit={handleSaveGoogle}>
              <div className="form-group mb-2">
                <label className="form-label" htmlFor="g-cal-id">
                  Calendar ID
                </label>
                <input
                  id="g-cal-id"
                  type="text"
                  className="form-control"
                  value={googleCalendarId}
                  onChange={(e) => setGoogleCalendarId(e.target.value)}
                  placeholder="primary"
                />
              </div>
              {(showGoogleTokenField || !credentialsConfigured || clinic?.externalCalendarProvider !== "google_calendar") && (
                <div className="form-group mb-2">
                  <label className="form-label" htmlFor="g-refresh">
                    OAuth refresh token
                  </label>
                  <input
                    id="g-refresh"
                    type="password"
                    className="form-control"
                    value={googleRefreshToken}
                    onChange={(e) => setGoogleRefreshToken(e.target.value)}
                    placeholder={
                      credentialsConfigured && clinic?.externalCalendarProvider === "google_calendar"
                        ? "Paste new refresh token to replace"
                        : "Google OAuth refresh token"
                    }
                    autoComplete="off"
                  />
                </div>
              )}
              <Button color="primary" type="submit" disabled={savingGoogle}>
                {savingGoogle ? "Saving…" : "Save Google Calendar"}
              </Button>
              {showGoogleTokenField ? (
                <Button
                  color="link"
                  type="button"
                  className="ml-2"
                  onClick={() => {
                    setShowGoogleTokenField(false);
                    setGoogleRefreshToken("");
                  }}
                >
                  Cancel token change
                </Button>
              ) : null}
            </form>
          </Col>

          {connected && (
            <Col md="12" className="mt-3">
              <Button color="danger" outline onClick={handleDisconnect} disabled={saving}>
                Disconnect calendar
              </Button>
            </Col>
          )}
        </Row>
      </Block>

      <Modal isOpen={createModalOpen} toggle={() => setCreateModalOpen(false)} className="modal-lg">
        <ModalHeader toggle={() => setCreateModalOpen(false)}>Create platform slot</ModalHeader>
        <form onSubmit={submitCreateSlot}>
          <ModalBody>
            <p className="text-soft small">Times use your browser local timezone. Ensure they match the clinic window you need.</p>
            <div className="form-group mb-2">
              <label className="form-label" htmlFor="m-slot-start">
                Start
              </label>
              <input
                id="m-slot-start"
                type="datetime-local"
                className="form-control"
                value={slotStartLocal}
                onChange={(e) => setSlotStartLocal(e.target.value)}
                required
              />
            </div>
            <div className="form-group mb-2">
              <label className="form-label" htmlFor="m-slot-end">
                End
              </label>
              <input
                id="m-slot-end"
                type="datetime-local"
                className="form-control"
                value={slotEndLocal}
                onChange={(e) => setSlotEndLocal(e.target.value)}
                required
              />
            </div>
            <div className="form-group mb-2">
              <label className="form-label" htmlFor="m-slot-pract">
                Practitioner (optional)
              </label>
              <select
                id="m-slot-pract"
                className="form-control"
                value={slotPractitionerId}
                onChange={(e) => setSlotPractitionerId(e.target.value)}
              >
                <option value="">— None —</option>
                {practitioners.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.displayName || p._id}
                  </option>
                ))}
              </select>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={creatingSlot}>
              {creatingSlot ? "Creating…" : "Create slot"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={detailModalOpen} toggle={() => setDetailModalOpen(false)}>
        <ModalHeader toggle={() => setDetailModalOpen(false)}>Slot details</ModalHeader>
        <ModalBody>
          {detailSlot ? (
            <>
              <p>
                <strong>Status:</strong> {detailSlot.status}
              </p>
              <p>
                <strong>Source:</strong>{" "}
                {detailSlot.slotSource === "external_inbound" ? "External sync" : "Platform"}
              </p>
              <p>
                <strong>Start:</strong> {new Date(detailSlot.startAt).toLocaleString()}
              </p>
              <p>
                <strong>End:</strong> {new Date(detailSlot.endAt).toLocaleString()}
              </p>
              <p>
                <strong>Practitioner:</strong> {detailSlot.practitioner?.displayName || "—"}
              </p>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" onClick={() => setDetailModalOpen(false)}>
            Close
          </Button>
          {detailSlot?.status === "available" ? (
            <Button color="danger" type="button" onClick={() => handleDeleteSlot(detailSlot)}>
              Delete slot
            </Button>
          ) : null}
        </ModalFooter>
      </Modal>
    </Content>
  );
};

export default ClinicCalendarAdmin;
